import { Fragment, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type LicenceRow, type RoleRow, type UserRow } from '../lib/api'

/**
 * A time-boxed role, as the account list now reports it.
 *
 * `user_roles.expires_at` is what makes a visiting fellowship possible: a role
 * that lapses on its own rather than one somebody has to remember to revoke.
 * The account carries the soonest of its grants.
 */
interface Grant {
  role: string
  expires_at: string | null
}

type Account = UserRow & { role_grants?: Grant[]; expires_at?: string | null }

interface UserSummary {
  active: number
  total: number
  expiring_this_month: number
}

/** Whole days from now, negative once it has passed. */
function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}
import { Empty, ErrorNote } from '../components/Page'
import { Card, CardBar, CardFoot, MenuItem, RowMenu, ago } from '../components/admin/Console'

/**
 * Accounts and the roles they hold.
 *
 * A librarian sees only their own institution; an administrator sees everyone.
 * That scoping is enforced in the API rather than here, because a hidden row
 * is not an access control.
 *
 * The date scope column is the licence the account reads under, not a property
 * of the account: access is scoped by institution and by licence, and showing
 * it on the person is what makes "why can this reader not open 1968" a
 * one-glance question.
 */
export function UsersPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)

  const users = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () =>
      api.get<Account[]>(`/admin/users${search ? `?q=${encodeURIComponent(search)}` : ''}`),
  })

  // The header counts the whole institution, not the page. A search that
  // narrows the table must not appear to shrink the library's roll.
  const summary = useQuery({
    queryKey: ['admin', 'users', 'summary'],
    queryFn: () => api.get<UserSummary>('/admin/users/summary'),
  })

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api.get<RoleRow[]>('/admin/roles'),
  })

  const licences = useQuery({
    queryKey: ['admin', 'licences'],
    queryFn: () => api.get<LicenceRow[]>('/admin/licences'),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })

  const create = useMutation({
    mutationFn: (input: { email: string; display_name: string; password: string; roles: string[] }) =>
      api.post<Account>('/admin/users', input),
    onSuccess: () => {
      setCreating(false)
      void invalidate()
    },
  })

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      api.patch<Account>(`/admin/users/${input.id}`, { status: input.status }),
    onSuccess: invalidate,
  })

  /**
   * Granting a role, optionally for a fixed term.
   *
   * `user_roles.expires_at` is what makes a visiting fellowship or a
   * reading-room booking possible: a role that lapses on its own rather than
   * one somebody has to remember to revoke.
   */
  const grant = useMutation({
    mutationFn: (input: { id: string; role_key: string; days?: number }) => {
      const expires = input.days
        ? new Date(Date.now() + input.days * 86_400_000).toISOString()
        : null
      return api.post<Account>(`/admin/users/${input.id}/roles`, {
        role_key: input.role_key,
        expires_at: expires,
      })
    },
    onSuccess: invalidate,
  })

  const revoke = useMutation({
    mutationFn: (input: { id: string; role_key: string }) =>
      api.del<Account>(`/admin/users/${input.id}/roles/${input.role_key}`),
    onSuccess: invalidate,
  })

  const rows = users.data ?? []
  const roleName = useMemo(() => {
    const map = new Map<string, string>()
    for (const role of roles.data ?? []) map.set(role.key, role.name)
    return map
  }, [roles.data])

  /** The date range the account's institution holds a licence over. */
  function scopeOf(user: Account): string {
    const held = (licences.data ?? []).filter(
      (licence) => licence.institution_id === user.institution_id && licence.active,
    )
    if (!held.length) return 'Metadata only'
    const from = held
      .map((licence) => licence.starts_on)
      .filter(Boolean)
      .sort()[0]
    const to = held
      .map((licence) => licence.ends_on)
      .filter(Boolean)
      .sort()
      .slice(-1)[0]
    if (!from && !to) return 'All'
    return `${from?.slice(0, 4) ?? '…'}–${to?.slice(0, 4) ?? 'open'}`
  }

  const active = summary.data?.active ?? rows.filter((user) => user.status === 'active').length
  const expiring = summary.data?.expiring_this_month ?? 0

  function exportCsv() {
    const header = ['email', 'display_name', 'roles', 'status', 'auth_provider', 'last_login_at']
    const lines = [header.join(',')]
    for (const user of rows) {
      lines.push(
        [
          user.email,
          `"${user.display_name.replace(/"/g, '""')}"`,
          `"${user.roles.join(' ')}"`,
          user.status,
          user.auth_provider,
          user.last_login_at ?? '',
        ].join(','),
      )
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'users.csv'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <ErrorNote error={create.error ?? grant.error ?? revoke.error ?? setStatus.error} />

      <Card>
        <CardBar>
          <h1 className="text-[23px] font-semibold leading-[1.1] tracking-[-0.012em]">Users</h1>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.04em] text-ink2">
            {active.toLocaleString()} active ·{' '}
            <span className={expiring ? 'text-mark' : undefined}>
              {expiring.toLocaleString()} expiring this month
            </span>
          </div>
          <div className="ml-3.5 flex h-8 max-w-[280px] flex-1 items-center gap-2 rounded-[--radius-md] border border-rule2 px-[11px]">
            {/* Drawn, not typed: the vendored mono subset carries no U+2315. */}
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="flex-none text-ink3">
              <circle cx="5" cy="5" r="3.4" stroke="currentColor" strokeWidth="1.2" />
              <path d="M7.6 7.6 10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <input
              className="w-full border-0 bg-transparent p-0 text-[13.5px] outline-none placeholder:text-ink4"
              placeholder="Name, email or barcode"
              aria-label="Search accounts"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="ml-auto flex flex-wrap gap-[7px]">
            <button
              type="button"
              className="btn-secondary"
              disabled
              title="There is no bulk-import endpoint in this build. Accounts arrive from the identity provider on first sign-in, or are added one at a time here."
            >
              Import CSV
            </button>
            <button type="button" className="btn-secondary" onClick={exportCsv} disabled={!rows.length}>
              Export
            </button>
            <button type="button" className="btn-primary" onClick={() => setCreating((v) => !v)}>
              {creating ? 'Cancel' : 'Add a user'}
            </button>
          </div>
        </CardBar>

        {creating && (
          <NewUserForm
            roles={roles.data ?? []}
            pending={create.isPending}
            onSubmit={(input) => create.mutate(input)}
          />
        )}

        {users.isLoading ? (
          <Empty>Reading the accounts.</Empty>
        ) : !rows.length ? (
          <Empty>
            No account matches that search. Accounts arrive from the identity provider on first
            sign-in; a local one is added with the button above.
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <div
              className="grid min-w-[900px]"
              style={{
                gridTemplateColumns: '1.25fr 1.6fr 0.85fr 0.9fr 0.62fr 0.95fr 0.8fr auto',
              }}
            >
              {['User', 'Email', 'Role', 'Date scope', 'Sign-in', 'Status', 'Last seen', ''].map(
                (head, index) => (
                  <div
                    key={head || `blank-${index}`}
                    className="border-b border-n300 bg-surface px-3.5 py-[9px] text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink2"
                  >
                    {head}
                  </div>
                ),
              )}

              {rows.map((user) => {
                const cell = 'border-b border-n200 px-3.5 py-[9px]'
                const mono = `${cell} font-mono text-[11.5px] text-ink2`
                const serif = `${cell} text-[14px] text-ink2`
                const suspended = user.status === 'suspended' || user.status === 'disabled'
                // A role that lapses is the one thing on this row a librarian
                // has to act on, so it takes the proof-reader's magenta and
                // says how long is left rather than printing a date to work out.
                const lapse =
                  user.expires_at ??
                  (user.role_grants ?? [])
                    .map((grant) => grant.expires_at)
                    .filter((value): value is string => !!value)
                    .sort()[0] ??
                  null
                const left = lapse ? daysUntil(lapse) : null
                return (
                  <Fragment key={user.id}>
                    <div className={`${cell} truncate text-[14px] font-semibold`}>
                      {user.display_name}
                    </div>
                    <div className={`${mono} truncate`} title={user.email}>
                      {user.email}
                    </div>
                    <div className={`${serif} truncate`}>
                      {user.roles.map((key) => roleName.get(key) ?? key).join(', ') || 'No role'}
                    </div>
                    <div className={mono}>{scopeOf(user)}</div>
                    <div className={serif}>{user.auth_provider === 'local' ? 'Local' : user.auth_provider.toUpperCase()}</div>
                    <div
                      className={`${cell} text-[14px]`}
                      style={{
                        color:
                          suspended || (left !== null && left <= 30)
                            ? 'var(--color-accent-2-700)'
                            : 'var(--color-neutral-800)',
                      }}
                      title={lapse ? `The role lapses on ${lapse.slice(0, 10)}` : undefined}
                    >
                      {left !== null && left >= 0
                        ? `Expires in ${left} ${left === 1 ? 'day' : 'days'}`
                        : left !== null
                          ? 'Expired'
                          : user.status === 'active'
                            ? 'Active'
                            : user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </div>
                    <div className={mono}>{ago(user.last_login_at)}</div>
                    <div className={`${cell} text-right`}>
                      <RowMenu label={`Actions for ${user.display_name}`}>
                        {(roles.data ?? [])
                          .filter((role) => !user.roles.includes(role.key))
                          .map((role) => (
                            <MenuItem
                              key={role.key}
                              onClick={() => grant.mutate({ id: user.id, role_key: role.key })}
                              trailing={{
                                label: '30 d',
                                title: `Grant ${role.name} for thirty days, after which it lapses on its own`,
                                onClick: () =>
                                  grant.mutate({ id: user.id, role_key: role.key, days: 30 }),
                              }}
                            >
                              Grant {role.name}
                            </MenuItem>
                          ))}
                        {user.roles.map((key) => (
                          <MenuItem
                            key={key}
                            tone="mark"
                            onClick={() => revoke.mutate({ id: user.id, role_key: key })}
                          >
                            Revoke {roleName.get(key) ?? key}
                          </MenuItem>
                        ))}
                        <MenuItem
                          tone="mark"
                          onClick={() =>
                            setStatus.mutate({
                              id: user.id,
                              status: user.status === 'active' ? 'suspended' : 'active',
                            })
                          }
                        >
                          {user.status === 'active' ? 'Suspend this account' : 'Reinstate this account'}
                        </MenuItem>
                      </RowMenu>
                    </div>
                  </Fragment>
                )
              })}
            </div>
          </div>
        )}

        <CardFoot>
          <div>
            SHOWING {rows.length} OF {(summary.data?.total ?? rows.length).toLocaleString()}
          </div>
          <div className="text-rule2">|</div>
          <div>ROLES ARE DATABASE-DRIVEN · A ROLE CAN BE RECOMPOSED WITHOUT A DEPLOY</div>
          <div className="ml-auto text-ink">
            USER_ROLES.EXPIRES_AT MAKES A TIME-BOXED ROLE POSSIBLE
          </div>
        </CardFoot>
      </Card>

    </>
  )
}

function NewUserForm({
  roles,
  pending,
  onSubmit,
}: {
  roles: RoleRow[]
  pending: boolean
  onSubmit: (input: {
    email: string
    display_name: string
    password: string
    roles: string[]
  }) => void
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('reader')

  function submit(event: FormEvent) {
    event.preventDefault()
    onSubmit({ email, display_name: name, password, roles: role ? [role] : [] })
  }

  return (
    <form onSubmit={submit} className="grid gap-3 border-b border-n300 bg-surface p-4 md:grid-cols-4">
      <div>
        <label className="label" htmlFor="new-email">
          Email
        </label>
        <input
          id="new-email"
          className="input-dense font-mono"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="new-name">
          Display name
        </label>
        <input
          id="new-name"
          className="input-dense"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="new-password">
          Password, at least 12 characters
        </label>
        <input
          id="new-password"
          className="input-dense font-mono"
          type="password"
          minLength={12}
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="new-role">
          Role
        </label>
        <select
          id="new-role"
          className="input-dense"
          value={role}
          onChange={(event) => setRole(event.target.value)}
        >
          {roles.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.name}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-4">
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending ? 'Creating…' : 'Create account'}
        </button>
      </div>
    </form>
  )
}
